import React, { useState, useCallback, useEffect } from "react";
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardMedia from '@mui/material/CardMedia';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import PropTypes from "prop-types";
import CoinbaseModal from './CoinbaseModal';
import CoinbaseProModal from './CoinbaseProModal';

// Helper to get CSRF token
const getCsrfToken = () => {
  const token = document.querySelector('meta[name="csrf-token"]')?.content;
  return token;
};

// --- Wallet Data --- 
const wallets = [
  { name: 'Coinbase', logo: '/exchanges/coinbase.png', comingSoon: false, id: 'coinbase' },
  { name: 'Coinbase Pro', logo: '/exchanges/coinbase_pro.png', comingSoon: false, id: 'coinbase_pro' },
  { name: 'Binance', logo: '/exchanges/binance.png', comingSoon: true, id: 'binance' },
  { name: 'Binance US', logo: '/exchanges/binance.png', comingSoon: true, id: 'binance_us' },
  { name: 'Solana', logo: '/exchanges/solana.png', comingSoon: true, id: 'solana' },
];

const SourcesManagement = ({ currentCompany, onWalletConnect, onSourcesChange }) => {
  const [searchTerm, setSearchTerm] = useState('');
  
  // States for Coinbase modal
  const [coinbaseModalOpen, setCoinbaseModalOpen] = useState(false);
  const [isCoinbaseSaving, setIsCoinbaseSaving] = useState(false);
  const [coinbaseError, setCoinbaseError] = useState(null);
  
  // States for Coinbase Pro modal
  const [coinbaseProModalOpen, setCoinbaseProModalOpen] = useState(false);
  const [isCoinbaseProSaving, setIsCoinbaseProSaving] = useState(false);
  const [coinbaseProError, setCoinbaseProError] = useState(null);
  
  // General states
  const [sources, setSources] = useState([]);
  const [isSourcesLoading, setIsSourcesLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  
  // States for edit/delete functionality
  const [editingSource, setEditingSource] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [sourceToDelete, setSourceToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch sources for the company
  const fetchSources = useCallback(async () => {
    if (!currentCompany?.id) return;

    setIsSourcesLoading(true);
    const sourcesUrl = `/api/internal/companies/${currentCompany.id}/sources`;
    const csrfToken = getCsrfToken();

    try {
      const response = await fetch(sourcesUrl, {
        headers: {
          'Accept': 'application/json',
          ...(csrfToken && { 'X-CSRF-Token': csrfToken })
        }
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setSources(data || []);
      
      // Notify parent component of sources count change
      if (onSourcesChange) {
        onSourcesChange(data?.length || 0);
      }
    } catch (err) {
      console.error("Failed to fetch sources:", err);
      setSources([]);
      if (onSourcesChange) {
        onSourcesChange(0);
      }
    } finally {
      setIsSourcesLoading(false);
    }
  }, [currentCompany?.id, onSourcesChange]);

  // Fetch sources on mount
  useEffect(() => {
    fetchSources();
  }, [fetchSources]);

  // --- Event Handlers ---
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  // === COINBASE HANDLERS ===
  const handleOpenCoinbaseModal = () => {
    setEditingSource(null);
    setCoinbaseModalOpen(true);
    setCoinbaseError(null);
  };

  const handleCloseCoinbaseModal = () => {
    setCoinbaseModalOpen(false);
    setCoinbaseError(null);
    setEditingSource(null);
  };

  const handleSaveCoinbaseSource = async (sourceData) => {
    setIsCoinbaseSaving(true);
    setCoinbaseError(null);

    const csrfToken = getCsrfToken();
    if (!currentCompany?.id) {
      setCoinbaseError("Company ID is missing. Cannot save source.");
      setIsCoinbaseSaving(false);
      return;
    }

    if (!csrfToken) {
      setCoinbaseError("CSRF token not found. Please refresh the page.");
      setIsCoinbaseSaving(false);
      return;
    }

    const isEditing = !!editingSource;
    const url = isEditing 
      ? `/api/internal/companies/${currentCompany.id}/sources/${editingSource.id}`
      : `/api/internal/companies/${currentCompany.id}/sources`;
    const method = isEditing ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({ source: sourceData })
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.error || `HTTP error! status: ${response.status}`);
      }

      setCoinbaseModalOpen(false);
      setEditingSource(null);
      setSnackbarMessage(responseData.message || `Coinbase source ${isEditing ? 'updated' : 'added'} successfully!`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      await fetchSources();

    } catch (err) {
      console.error("Failed to save Coinbase source:", err);
      setCoinbaseError(`Failed to save source: ${err.message}`);
    } finally {
      setIsCoinbaseSaving(false);
    }
  };

  // === COINBASE PRO HANDLERS ===
  const handleOpenCoinbaseProModal = () => {
    setEditingSource(null);
    setCoinbaseProModalOpen(true);
    setCoinbaseProError(null);
  };

  const handleCloseCoinbaseProModal = () => {
    setCoinbaseProModalOpen(false);
    setCoinbaseProError(null);
    setEditingSource(null);
  };

  const handleSaveCoinbaseProSource = async (sourceData) => {
    setIsCoinbaseProSaving(true);
    setCoinbaseProError(null);

    const csrfToken = getCsrfToken();
    if (!currentCompany?.id) {
      setCoinbaseProError("Company ID is missing. Cannot save source.");
      setIsCoinbaseProSaving(false);
      return;
    }

    if (!csrfToken) {
      setCoinbaseProError("CSRF token not found. Please refresh the page.");
      setIsCoinbaseProSaving(false);
      return;
    }

    const isEditing = !!editingSource;
    const url = isEditing 
      ? `/api/internal/companies/${currentCompany.id}/sources/${editingSource.id}`
      : `/api/internal/companies/${currentCompany.id}/sources`;
    const method = isEditing ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({ source: sourceData })
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.error || `HTTP error! status: ${response.status}`);
      }

      setCoinbaseProModalOpen(false);
      setEditingSource(null);
      setSnackbarMessage(responseData.message || `Coinbase Pro source ${isEditing ? 'updated' : 'added'} successfully!`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      await fetchSources();

    } catch (err) {
      console.error("Failed to save Coinbase Pro source:", err);
      setCoinbaseProError(`Failed to save source: ${err.message}`);
    } finally {
      setIsCoinbaseProSaving(false);
    }
  };

  // === EDIT/DELETE HANDLERS ===
  const handleEditSource = (source) => {
    setEditingSource(source);
    
    if (source.source_name === 'coinbase') {
      setCoinbaseModalOpen(true);
      setCoinbaseError(null);
    } else if (source.source_name === 'coinbase_pro') {
      setCoinbaseProModalOpen(true);
      setCoinbaseProError(null);
    }
  };

  const handleDeleteSource = (source) => {
    setSourceToDelete(source);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!sourceToDelete) return;

    setIsDeleting(true);
    const csrfToken = getCsrfToken();

    try {
      const response = await fetch(`/api/internal/companies/${currentCompany.id}/sources/${sourceToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      setSnackbarMessage("Source deleted successfully!");
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      await fetchSources();

    } catch (err) {
      console.error("Failed to delete source:", err);
      setSnackbarMessage(`Failed to delete source: ${err.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsDeleting(false);
      setDeleteConfirmOpen(false);
      setSourceToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setSourceToDelete(null);
  };

  const handleWalletClick = (walletId, comingSoon) => {
    if (comingSoon) {
      console.log(`Wallet ${walletId} is coming soon.`);
      return;
    }

    // Handle different wallet types
    if (walletId === 'coinbase') {
      handleOpenCoinbaseModal();
      return;
    }
    
    if (walletId === 'coinbase_pro') {
      handleOpenCoinbaseProModal();
      return;
    }

    console.log(`Connecting to wallet ${walletId}...`);
    // Call the optional onWalletConnect callback for other wallet types
    if (onWalletConnect) {
      onWalletConnect(walletId);
    }
  };

  // Filter wallets based on search term
  const filteredWallets = wallets.filter(wallet => 
    wallet.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get source count for a wallet type
  const getSourceCount = (walletId) => {
    return sources.filter(source => source.source_name === walletId).length;
  };

  return (
    <React.Fragment>
      {/* Coinbase Modal */}
      <CoinbaseModal
        open={coinbaseModalOpen}
        onClose={handleCloseCoinbaseModal}
        onSave={handleSaveCoinbaseSource}
        isLoading={isCoinbaseSaving}
        error={coinbaseError}
        editingSource={editingSource?.source_name === 'coinbase' ? editingSource : null}
      />

      {/* Coinbase Pro Modal */}
      <CoinbaseProModal
        open={coinbaseProModalOpen}
        onClose={handleCloseCoinbaseProModal}
        onSave={handleSaveCoinbaseProSource}
        isLoading={isCoinbaseProSaving}
        error={coinbaseProError}
        editingSource={editingSource?.source_name === 'coinbase_pro' ? editingSource : null}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Source
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{sourceToDelete?.display_name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} disabled={isDeleting}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for feedback */} 
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Configured Sources Section */}
      {!isSourcesLoading && sources.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'medium' }}>
            Configured Sources
          </Typography>
          <Stack spacing={2}>
            {sources.map((source) => (
              <Card key={source.id} variant="outlined" sx={{ borderRadius: '8px' }}>
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: 32, height: 32, mr: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CardMedia
                        component="img"
                        image={source.source_name === 'coinbase_pro' ? '/exchanges/coinbase_pro.png' : source.source_name === 'coinbase' ? '/exchanges/coinbase.png' : '/exchanges/default.png'}
                        alt={source.source_name}
                        sx={{ 
                          maxWidth: '100%',
                          maxHeight: '100%',
                          width: 'auto',
                          height: 'auto',
                          objectFit: 'contain' 
                        }} 
                      />
                    </Box>
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {source.display_name}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Chip 
                          label={source.source_name === 'coinbase_pro' ? 'Coinbase Pro' : source.source_name === 'coinbase' ? 'Coinbase' : source.source_name} 
                          size="small" 
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton 
                      onClick={() => handleEditSource(source)} 
                      size="small"
                      sx={{ color: 'primary.main' }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      onClick={() => handleDeleteSource(source)} 
                      size="small"
                      sx={{ color: 'error.main' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Card>
            ))}
          </Stack>
        </Box>
      )}

      {/* Add New Sources Section */}
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'medium', mt: sources.length > 0 ? 2 : 0 }}>
        Add New Sources
      </Typography>

      {/* Search Bar */} 
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search"
        value={searchTerm}
        onChange={handleSearchChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'text.secondary' }} />
            </InputAdornment>
          ),
          sx: { borderRadius: '8px', bgcolor: 'common.white' }
        }}
        sx={{ mb: 4 }}
      />

      {/* Wallet Grid */} 
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {filteredWallets.map((wallet) => (
          <Grid item xs={12} sm={6} md={6} key={wallet.id}>
            <Card 
              variant="outlined" 
              sx={{ 
                borderRadius: '8px', 
                bgcolor: wallet.comingSoon ? '#EEEEEE' : 'common.white',
                opacity: wallet.comingSoon ? 0.6 : 1,
              }}
            >
              <CardActionArea 
                onClick={() => handleWalletClick(wallet.id, wallet.comingSoon)}
                disabled={wallet.comingSoon}
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  p: 1.5,
                  minHeight: 64,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 32, height: 32, mr: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CardMedia
                      component="img"
                      image={wallet.logo}
                      alt={wallet.name}
                      sx={{ 
                        maxWidth: '100%',
                        maxHeight: '100%',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain' 
                      }} 
                    />
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {wallet.name}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  {wallet.comingSoon && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                      Coming Soon
                    </Typography>
                  )}
                  {!wallet.comingSoon && getSourceCount(wallet.id) > 0 && (
                    <Chip 
                      icon={<AddIcon />}
                      label={`Add Another (${getSourceCount(wallet.id)} configured)`}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  )}
                </Box>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
        {filteredWallets.length === 0 && (
          <Grid item xs={12}>
            <Typography sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
              No matching wallets found.
            </Typography>
          </Grid>
        )}
      </Grid>
    </React.Fragment>
  );
};

SourcesManagement.propTypes = {
  currentCompany: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }),
  onWalletConnect: PropTypes.func, // Callback for when a wallet is clicked (for custom handling)
  onSourcesChange: PropTypes.func // Callback for when sources count changes
};

SourcesManagement.defaultProps = {
  currentCompany: null,
  onWalletConnect: null,
  onSourcesChange: null
};

export default SourcesManagement; 
